/**
 * GroupApiService
 * Handles all group-related API endpoints
 */

import type { HttpClient } from './HttpClient';
import type {
  Group,
  GroupRequestCreate,
  GroupRequest,
  GroupRequestListResponse,
  GroupRequestApproval,
  User
} from '../../api/data-contracts';

/**
 * Service for group operations
 */
export class GroupApiService {
  constructor(private readonly httpClient: HttpClient) {}

  /**
   * Get current user's groups
   */
  public async getMyGroups(status?: string): Promise<Group[]> {
    const params = status ? { status } : undefined;
    return this.httpClient.get<Group[]>('/groups/my-groups', { params });
  }

  /**
   * Get all groups (admin only)
   */
  public async getAllGroups(): Promise<Group[]> {
    return this.httpClient.get<Group[]>('/groups');
  }

  /**
   * Get a specific group by ID
   */
  public async getGroup(groupId: string): Promise<Group> {
    return this.httpClient.get<Group>(`/groups/${groupId}`);
  }

  /**
   * Create a new group
   */
  public async createGroup(groupData: Partial<Group>): Promise<Group> {
    return this.httpClient.post<Group>('/groups', groupData);
  }

  /**
   * Update a group
   */
  public async updateGroup(
    groupId: string,
    updates: Partial<Group>
  ): Promise<Group> {
    return this.httpClient.put<Group>(`/groups/${groupId}`, updates);
  }

  /**
   * Delete a group
   */
  public async deleteGroup(groupId: string): Promise<{ message: string }> {
    return this.httpClient.delete<{ message: string }>(`/groups/${groupId}`);
  }

  /**
   * Add member to group
   */
  public async addGroupMember(
    groupId: string,
    userId: string,
    role: string = 'member'
  ): Promise<{ message: string }> {
    return this.httpClient.post(`/groups/${groupId}/members`, {
      user_id: userId,
      role,
    });
  }

  /**
   * Remove member from group
   */
  public async removeGroupMember(groupId: string, userId: string): Promise<{ message: string }> {
    return this.httpClient.delete(`/groups/${groupId}/members/${userId}`);
  }

  /**
   * Update member role in group
   */
  public async updateGroupMemberRole(
    groupId: string,
    userId: string,
    role: string
  ): Promise<{ message: string }> {
    return this.httpClient.put(`/groups/${groupId}/members/${userId}`, {
      role,
    });
  }

  // ============================================================================
  // Group Request Endpoints
  // ============================================================================

  /**
   * Create a new group creation request
   */
  public async createGroupRequest(requestData: GroupRequestCreate): Promise<GroupRequest> {
    return this.httpClient.post<GroupRequest>('/groups/requests', requestData);
  }

  /**
   * Get current user's group requests (paginated)
   */
  public async getMyGroupRequests(page: number = 1, pageSize: number = 50): Promise<GroupRequestListResponse> {
    return this.httpClient.get<GroupRequestListResponse>('/groups/requests/my-requests', {
      params: { page, page_size: pageSize }
    });
  }

  /**
   * Get pending group requests (moderator/admin only, paginated)
   */
  public async getPendingGroupRequests(page: number = 1, pageSize: number = 50): Promise<GroupRequestListResponse> {
    return this.httpClient.get<GroupRequestListResponse>('/groups/requests/pending', {
      params: { page, page_size: pageSize }
    });
  }

  /**
   * Get a specific group request by ID
   */
  public async getGroupRequest(requestId: string): Promise<GroupRequest> {
    return this.httpClient.get<GroupRequest>(`/groups/requests/${requestId}`);
  }

  /**
   * Approve a group request
   */
  public async approveGroupRequest(requestId: string, approval: GroupRequestApproval): Promise<GroupRequest> {
    return this.httpClient.post<GroupRequest>(`/groups/requests/${requestId}/approve`, approval);
  }

  /**
   * Reject a group request
   */
  public async rejectGroupRequest(requestId: string, rejection: GroupRequestApproval): Promise<GroupRequest> {
    return this.httpClient.post<GroupRequest>(`/groups/requests/${requestId}/reject`, rejection);
  }

  /**
   * Cancel a group request (requester only)
   */
  public async cancelGroupRequest(requestId: string): Promise<GroupRequest> {
    return this.httpClient.delete<GroupRequest>(`/groups/requests/${requestId}`);
  }

  /**
   * Mark a request as being viewed (5-minute lock)
   */
  public async markRequestAsViewing(requestId: string): Promise<GroupRequest> {
    return this.httpClient.post<GroupRequest>(`/groups/requests/${requestId}/mark-viewing`, {});
  }

  /**
   * Clear viewing status on a request
   */
  public async clearRequestViewing(requestId: string): Promise<GroupRequest> {
    return this.httpClient.post<GroupRequest>(`/groups/requests/${requestId}/clear-viewing`, {});
  }

  /**
   * Get eligible approvers (admin/moderator users)
   */
  public async getEligibleApprovers(): Promise<User[]> {
    return this.httpClient.get<User[]>('/groups/requests/eligible-approvers');
  }
}
